import { isAxiosError, AxiosError } from 'axios'
import { inject, Plugin } from 'vue'
import { AppNotifyTypes } from '~/lib/notify'
import { HandleFetchErrorKey } from '~/lib/symbols'
import { normaliseArray } from '~/helper/data-transform'
import type { H3Error } from 'h3'

declare module 'vue' {
  interface ComponentCustomProperties {
    $handleFetchError: ReturnType<typeof createFetchErrorHandler>
  }
}
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $handleFetchError: ReturnType<typeof createFetchErrorHandler>
  }
}

export type AppFetchError = Error | AxiosError<Record<string | number, any> | string | any[]> | string
export type FetchErrorHandler = ReturnType<typeof createFetchErrorHandler>
type Notifier = (message: string, type: AppNotifyTypes) => any
export type AppErrorMessage = {
  ofError: string
  ofResponse: string[]
  info?: string
  stack?: string
}

export function injectHandleFetchError() {
  const handler = inject(HandleFetchErrorKey)
  if (!handler) {
    throw Error('Fetch error handler missing')
  }
  return handler
}

/**
 * Unified try-catch handling for XHR-Requests
 */
export const AppFetchErrorHandlerPlugin: Plugin<Notifier> = {
  install: (app, notify) => {
    const handler = createFetchErrorHandler(notify)
    app.provide(HandleFetchErrorKey, handler)
    app.config.globalProperties.$handleFetchError = handler
  },
}

/**
 * Return function to extract error and display notification to user
 */
export function createFetchErrorHandler(notify?: Notifier) {
  return async (e: AppFetchError | unknown, type: AppNotifyTypes = 'error') => {
    try {
      const message = await extractErrorMessage(e as AppFetchError)
      if (import.meta.dev) {
        console.error(e)
      }
      outputMessage(notify, type, message, formatErrorMessage(message))
    } catch (error) {
      const fallbackMessage = error instanceof Error ? error.message : String(error)
      if (import.meta.dev) {
        console.error(e)
        console.error(error)
      }
      outputMessage(notify, type, fallbackMessage, fallbackMessage)
    }
  }
}

/**
 * Extract correct error message from ambiguous error
 */
export async function extractErrorMessage(error: AppFetchError): Promise<AppErrorMessage> {
  if (typeof error === 'string') {
    return {
      ofError: error,
      ofResponse: [],
    }
  }

  let ofError = error.name ? `${error.name}: ${error.message}` : error.message
  // Remove "Error: " prefix from default error messages
  if (ofError.startsWith('Error: ')) {
    ofError = ofError.slice(7)
  }
  const message: AppErrorMessage = {
    ofError,
    ofResponse: [],
    stack: error.stack,
  }

  if (isAxiosError(error) && error.response?.data) {
    // Add method + url
    if (error.response.config.url && error.response.config.method) {
      message.info = `${error.response.config.method.toUpperCase()} ${error.response.config.url}`
    }

    message.ofResponse.push(...(await extractResponseMessages(error.response.data)))
  } else if (isFrontendError(error)) {
    message.ofResponse.push(...extractFrontendErrors(error))
  }
  return message
}

async function extractResponseMessages(response: unknown): Promise<string[]> {
  const parsedResponse = await parseBlobResponse(response)

  if (isErrorDetailResponse(parsedResponse)) {
    return [parsedResponse.detail]
  }
  if (isBackendFatalError(parsedResponse)) {
    return extractBackendFatalErrors(parsedResponse)
  }
  if (isFrontendError(parsedResponse)) {
    return extractFrontendErrors(parsedResponse)
  }
  if (typeof parsedResponse === 'string') {
    return [parsedResponse]
  }
  if (isObject(parsedResponse)) {
    const messages: string[] = []
    for (const key in parsedResponse) {
      const value = parsedResponse[key as keyof typeof parsedResponse]
      messages.push(typeof value === 'string' ? value : JSON.stringify(value))
    }
    return messages
  }
  return []
}

function formatErrorMessage(message: AppErrorMessage): string {
  let formattedMessage = message.ofResponse.length ? message.ofResponse.join('<br>') : message.ofError
  if (message.info) {
    formattedMessage += `<br><small>${message.info}</small>`
  }
  return formattedMessage
}

function outputMessage(
  notify: Notifier | undefined,
  type: AppNotifyTypes,
  consoleMessage: unknown,
  notifyMessage: string,
) {
  if (notify) {
    notify(notifyMessage, type)
    return
  }

  logByType(type, consoleMessage)
}

function logByType(type: AppNotifyTypes, message: unknown) {
  if (type === 'error') console.error(message)
  if (type === 'info') console.info(message)
  if (type === 'warning') console.warn(message)
  if (type === 'success') console.log(message)
}

async function parseBlobResponse(response: unknown): Promise<unknown> {
  if (!isBlob(response)) {
    return response
  }

  const text = await response.text()
  if (!text) {
    return text
  }

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}
type BackendFatalError = {
  errors?: Record<string, string[] | string>
}

function extractBackendFatalErrors(error: BackendFatalError): string[] {
  if (error.errors) {
    return Object.entries(error.errors).map((e) => `${e[0]}:${normaliseArray(e[1]).flat().join(', ')}`)
  }
  return []
}
function isBackendFatalError(err: unknown): err is BackendFatalError {
  return isObject(err) && 'errors' in err && typeof err.errors === 'object' && err.errors !== null
}

function isErrorDetailResponse(res: any): res is {
  detail: string
} {
  return isObject(res) && 'detail' in res && typeof res.detail === 'string'
}

function isFrontendError(err: unknown): err is H3Error {
  return isObject(err) && 'message' in err && 'stack' in err
}

function extractFrontendErrors(err: H3Error): string[] {
  if (Array.isArray(err.stack)) {
    return err.stack
  }
  return [err.message]
}

function isObject(item: unknown): item is object {
  return typeof item === 'object' && item !== null
}

function isBlob(item: unknown): item is Blob {
  return typeof Blob !== 'undefined' && item instanceof Blob
}
